import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Command, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(name, email, password, companyName);
            toast.success("Welcome — your 14-day trial has started");
            nav("/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center p-8" data-testid="register-page">
                <div className="w-full max-w-sm animate-fade-up">
                    <Link to="/" className="flex items-center gap-2 mb-10" data-testid="brand-register">
                        <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center rounded-md">
                            <Command className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight">Closvio</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">M&A Platform</div>
                        </div>
                    </Link>
                    <h1 className="text-3xl font-display font-bold tracking-tight mb-1">Start your desk</h1>
                    <p className="text-sm text-muted-foreground mb-8">Create an account — 14-day trial, no card required.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Full name</Label>
                            <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" placeholder="Jane Broker" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="company" className="text-xs uppercase tracking-wider text-muted-foreground">Company / Firm name</Label>
                            <Input id="company" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="register-company-input" placeholder="Broker & Co" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" placeholder="you@firm.com" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                            <Input id="password" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" placeholder="At least 6 characters" />
                        </div>
                        <Button
                            type="submit" disabled={loading}
                            className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                            data-testid="register-submit-btn"
                        >
                            {loading ? "Creating…" : (<>Create account <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                        </Button>
                    </form>
                    <p className="text-sm text-muted-foreground mt-6">
                        Already have an account?{" "}
                        <Link to="/login" className="text-foreground font-medium underline underline-offset-4" data-testid="go-login-link">Sign in</Link>
                    </p>
                </div>
            </div>
            <div className="hidden lg:block relative bg-navy overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
                    <div className="max-w-lg">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Purpose-built for dealmakers</div>
                        <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
                            From mandate to close —<br />in one clean workspace.
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    );
}
